import { Directive, inject, output } from '@angular/core';
import { DropdownActions } from '../enums/base.enums';
import { BootstrapService } from '../services/bootstrap.service';

// Shared lifecycle for the feature "*-search-filter" dropdown components. A concrete filter supplies
// its filter object, its default factory, and the small set of CONTEXT fields the user can't change
// in the dropdown (org / timezone / scope ids) via preservedFilterKeys — onReset then clears only the
// user-facing fields and leaves that context intact. Decorated with @Directive() (no selector) so the
// output() and inject() are inherited by the concrete @Component subclasses.
@Directive()
export abstract class BaseSearchFilterComponent<TFilter extends object> {

    protected bootstrapService = inject(BootstrapService);

    public filterApplied = output<TFilter>();
    public filterCount: number = 0;

    //#region Extension points (implemented per component)

    // The filter the dropdown reads and mutates. Session-backed filters return
    // localFilter() ?? userSessionService.<x>Filter; input-driven filters return this.filter().
    protected abstract get activeFilter(): TFilter;

    // A FRESH default object (call the ListConstants factory / a defaultFilterFactory input). Never cache —
    // onReset copies its keys into the live filter, so a shared instance would leak mutated arrays.
    protected abstract getDefaultFilter(): TFilter;

    // Context fields the user cannot change in the dropdown, preserved across a reset. Fields absent from
    // getDefaultFilter() are preserved automatically (the reset copy never touches them), so only list the
    // context fields that DO appear in the default. Return [] when there are none.
    protected abstract get preservedFilterKeys(): (keyof TFilter)[];

    // The dropdown element selector, e.g. '#member-search-filter-dropdown'.
    protected abstract get dropdownSelector(): string;

    // Re-sync the component's local UI mirror (signals, text inputs, currency display fields) from
    // activeFilter after a reset.
    protected abstract syncStateFromFilter(): void;

    public abstract setFilterCount(): void;

    //#endregion Extension points

    //#region Optional hooks (no-op by default)

    // Re-apply auth-gated fields (e.g. includeDeletedX) that live outside the default factory.
    protected applyAuthGatedFields(): void { }

    // Persist the active filter for session-backed components; no-op for parent-owned filters.
    protected saveToStorage(): void { }

    // Prepare the filter immediately before it is applied (e.g. convert currency display values to minor
    // units, map a toggle onto the filter).
    protected beforeApply(): void { }

    //#endregion Optional hooks

    //#region Lifecycle

    public onReset(): void {
        const current = this.activeFilter;
        const defaultFilter = this.getDefaultFilter();
        const preserved = this.capturePreserved(current);

        // Overwrite only the keys the default factory defines — the user-facing fields. Context fields
        // absent from the default are left untouched; those present are restored immediately below.
        Object.keys(defaultFilter).forEach(key => {
            (current as any)[key] = (defaultFilter as any)[key];
        });

        this.restorePreserved(current, preserved);
        this.applyAuthGatedFields();
        this.syncStateFromFilter();
        this.saveToStorage();
        this.filterApplied.emit({ ...current } as TFilter);
        this.setFilterCount();
    }

    public onApply(): void {
        this.beforeApply();
        this.saveToStorage();
        this.filterApplied.emit({ ...this.activeFilter } as TFilter);
        this.setFilterCount();
        this.closeDropdown();
    }

    public closeDropdown(): void {
        this.bootstrapService.manageDropdown(this.dropdownSelector, DropdownActions.Hide);
    }

    //#endregion Lifecycle

    //#region Preservation helpers

    private capturePreserved(filter: TFilter): Partial<TFilter> {
        const preserved: Partial<TFilter> = {};
        for (const key of this.preservedFilterKeys) {
            preserved[key] = filter[key];
        }
        return preserved;
    }

    private restorePreserved(filter: TFilter, preserved: Partial<TFilter>): void {
        for (const key of this.preservedFilterKeys) {
            filter[key] = preserved[key] as TFilter[keyof TFilter];
        }
    }

    //#endregion Preservation helpers
}
